import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAdverts } from "@/hooks/content";
import { useTranslatedList } from "@/hooks/useTranslatedList";

export default function AdvertSection() {
  const { t } = useTranslation();
  const { items } = useAdverts();
  const adverts = useTranslatedList(items, ["title", "tagline", "description"]).filter(
    (a) => a.status === "active",
  );

  if (adverts.length === 0) return null;

  return (
    <section className="bg-white py-12 md:py-16">
      <div className="ltv-container">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-500 text-white">
            <i className="ri-megaphone-line text-lg" />
          </span>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-primary-900">
              {t("home.featuredAnnouncements")}
            </h2>
            <p className="text-sm text-foreground-600">{t("home.sponsoredCampaigns")}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {adverts.map((ad) => (
            <article
              key={ad.id}
              className="group grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 bg-background-50 rounded-lg border border-background-200 overflow-hidden"
            >
              {/* Flyer / image */}
              <div className="h-64 md:h-full min-h-[280px] bg-background-100 relative">
                {ad.image ? (
                  <img
                    src={ad.image}
                    alt={ad.title}
                    className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-foreground-300">
                    <i className="ri-image-line text-5xl" />
                  </div>
                )}
                <span className="absolute top-4 left-4 inline-flex items-center gap-1 bg-accent-500 text-white text-xs font-bold px-3 py-1.5 rounded-md uppercase tracking-wider">
                  <i className="ri-megaphone-line" />
                  {t("home.campaign")}
                </span>
              </div>

              {/* Copy */}
              <div className="flex flex-col justify-center p-6 md:p-10">
                <p className="text-accent-500 text-xs md:text-sm font-semibold uppercase tracking-widest mb-3">
                  {ad.tagline}
                </p>
                <h3 className="text-2xl md:text-3xl font-bold text-primary-900 leading-tight mb-4">
                  {ad.title}
                </h3>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6 line-clamp-6">
                  {ad.description}
                </p>
                {ad.link && (
                  <div className="mt-auto">
                    <Link
                      to={ad.link}
                      className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold px-5 py-2.5 rounded-md text-sm md:text-base whitespace-nowrap"
                    >
                      {t("home.learnMore")}
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}